import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { Bundle, Product } from '@/api/types';
import { Footer, SidePanel } from '@/components/PublicExtras';
import { UsdCalculator } from '@/components/UsdCalculator';

interface CheckoutStatus {
  id: string;
  status: 'PENDING' | 'PAID' | 'FAILED' | 'EXPIRED';
  productId: number;
  target: string;
  amount: string;
  currency: string;
  orderId: string | null;
  failureReason: string | null;
}

type Filter = 'ALL' | 'AIRTIME' | 'BUNDLES' | 'ZESA' | 'TELONE';

export function isMeterProduct(p: Product): boolean {
  return (p.targetType ?? '').toLowerCase().includes('meter');
}

export function isTeloneProduct(p: Product): boolean {
  return (p.network ?? '').toLowerCase().includes('telone');
}

/** The "number to recharge" field's label and placeholder depend on what's actually being paid. */
function targetFieldFor(p: Product): { label: string; placeholder: string } {
  if (isMeterProduct(p)) return { label: 'Meter number', placeholder: '12345678901' };
  if (isTeloneProduct(p)) return { label: 'Telone account number', placeholder: 'Account number' };
  return { label: 'Number to recharge', placeholder: '0772929223' };
}

/**
 * Unauthenticated: anyone with the link can pay for their own recharge. No console session, no
 * merchant API key — see PublicCheckoutController on the backend.
 *
 * Flow: pick a service (grid) → fill in the details (form) → pay with EcoCash → watch it settle.
 * Modelled loosely on magetsi.co.zw's layout (service grid, then a focused form per service).
 */
export function PublicCheckoutPage() {
  // The homepage can deep-link straight into a service, e.g. navigate('/pay', { state: { productId } }).
  const location = useLocation();
  const preselectedId = (location.state as { productId?: number } | null)?.productId ?? null;

  const [products, setProducts] = useState<Product[] | null>(null);
  const [filter, setFilter] = useState<Filter>('ALL');
  const [selectedId, setSelectedId] = useState<number | null>(preselectedId);

  const [target, setTarget] = useState('');
  const [amount, setAmount] = useState('');
  const [productCode, setProductCode] = useState('');
  const [bundles, setBundles] = useState<Bundle[] | null>(null);
  const [customerMsisdn, setCustomerMsisdn] = useState('');
  const [session, setSession] = useState<CheckoutStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Jackson serialises the backend's BigDecimal as a JSON number, not a string.
  const [fxRate, setFxRate] = useState<number | null>(null);

  useEffect(() => {
    fetch('/api/public/checkout/products')
      .then((r) => r.json())
      .then((data) => setProducts((data.products ?? []).filter((p: Product) => p.enabled)))
      .catch(() => setError('Could not load products. Please refresh the page.'));
    fetch('/api/public/checkout/fx-rate')
      .then((r) => r.json())
      .then((data) => setFxRate(data.usdToZwgRate ?? null))
      .catch(() => {}); // The calculator is a convenience; its absence shouldn't block paying.
  }, []);

  useEffect(() => {
    if (!session || session.status !== 'PENDING') return;
    const timer = setInterval(async () => {
      const res = await fetch(`/api/public/checkout/${session.id}`);
      if (res.ok) setSession(await res.json());
    }, 3000);
    return () => clearInterval(timer);
  }, [session]);

  const selected = products?.find((p) => p.productId === selectedId) ?? null;

  useEffect(() => {
    setBundles(null);
    setProductCode('');
    setAmount('');
    if (!selected?.requiresProductCode) return;
    fetch(`/api/public/checkout/products/${selected.productId}/bundles`)
      .then((r) => r.json())
      .then((data) => setBundles(data.bundles ?? []))
      .catch(() => setError('Could not load bundles for this product.'));
  }, [selected?.productId, selected?.requiresProductCode]);

  function pickBundle(code: string) {
    setProductCode(code);
    const bundle = bundles?.find((b) => b.productCode === code);
    if (bundle) setAmount(bundle.value);
  }

  const canSubmit = !!selected && !!target && !!amount && !!customerMsisdn
    && (!selected?.requiresProductCode || !!productCode);

  async function submit() {
    if (!selected) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/public/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId: selected.productId, target, amount,
          productCode: productCode || undefined, customerMsisdn,
        }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail ?? 'Something went wrong. Please try again.');
      setSession(body);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  function startOver() {
    setSession(null);
    setSelectedId(null);
    setTarget('');
    setAmount('');
    setProductCode('');
    setCustomerMsisdn('');
    setError(null);
  }

  const filtered = (products ?? []).filter((p) => {
    if (filter === 'ZESA') return isMeterProduct(p);
    if (filter === 'TELONE') return isTeloneProduct(p);
    if (filter === 'AIRTIME') return !p.requiresProductCode && !isMeterProduct(p) && !isTeloneProduct(p);
    if (filter === 'BUNDLES') return p.requiresProductCode && !isMeterProduct(p) && !isTeloneProduct(p);
    return true;
  });

  return (
    <div style={{ minHeight: '100vh', background: '#f5f6f8', fontFamily: 'system-ui, sans-serif' }}>
      <header className="pub-header" style={{
        background: '#1e2460', color: 'white',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontWeight: 700, fontSize: 18 }}>⚡ Recharge</span>
        <Link to="/pay/status" style={{ color: '#c7d2fe', fontSize: 13, textDecoration: 'none' }}>
          Check transaction →
        </Link>
      </header>

      <div className="pub-main" style={{
        display: 'grid', gap: 32, alignItems: 'start',
      }}>
      <div>
        {!session && !selected && (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4, color: '#1e2460' }}>
              Which service are you looking for?
            </h1>
            <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
              Pay with EcoCash and top up any number in seconds.
            </p>

            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              {(['ALL', 'AIRTIME', 'BUNDLES', 'ZESA', 'TELONE'] as Filter[]).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  style={{
                    padding: '6px 14px', borderRadius: 999, border: 'none', fontSize: 13, fontWeight: 600,
                    cursor: 'pointer',
                    background: filter === f ? '#1e2460' : 'white',
                    color: filter === f ? 'white' : '#444',
                    boxShadow: filter === f ? 'none' : '0 1px 2px rgba(0,0,0,0.08)',
                  }}
                >
                  {{ ALL: 'All', AIRTIME: 'Airtime', BUNDLES: 'Bundles', ZESA: 'Electricity', TELONE: 'Telone' }[f]}
                </button>
              ))}
            </div>

            {error && <p style={{ color: '#b91c1c', fontSize: 13, marginBottom: 12 }}>{error}</p>}

            <div className="pub-services" style={{ display: 'grid', gap: 10 }}>
              {filtered.map((p) => (
                <button
                  key={p.productId}
                  onClick={() => setSelectedId(p.productId)}
                  style={{
                    textAlign: 'left', background: 'white', border: '1px solid #e5e7eb', borderRadius: 10,
                    padding: '14px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 4,
                  }}
                >
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#1e2460', textTransform: 'uppercase' }}>
                    {p.network ?? p.category ?? 'Service'}
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#111' }}>{p.name}</span>
                  <span style={{ fontSize: 12, color: '#888' }}>
                    {isMeterProduct(p) ? 'Electricity' : isTeloneProduct(p) ? 'Telone'
                      : p.requiresProductCode ? 'Bundle' : 'Airtime'}{p.currency ? ` · ${p.currency}` : ''}
                  </span>
                </button>
              ))}
              {products && filtered.length === 0 && (
                <p style={{ gridColumn: '1 / -1', fontSize: 13, color: '#888' }}>Nothing here yet.</p>
              )}
              {!products && <p style={{ gridColumn: '1 / -1', fontSize: 13, color: '#888' }}>Loading…</p>}
            </div>
          </>
        )}

        {!session && selected && (
          <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <button onClick={() => setSelectedId(null)} style={{
              background: 'none', border: 'none', color: '#1e2460', fontSize: 13, fontWeight: 600,
              cursor: 'pointer', padding: 0, marginBottom: 12,
            }}>
              ← Back to services
            </button>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 2 }}>{selected.name}</h2>
            <p style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>
              {selected.network ?? ''}{selected.currency ? ` · ${selected.currency}` : ''}
            </p>

            {selected.requiresProductCode && (
              <Field label="Bundle">
                <select value={productCode} onChange={(e) => pickBundle(e.target.value)} style={inputStyle} disabled={!bundles}>
                  <option value="">{bundles ? 'Select a bundle…' : 'Loading bundles…'}</option>
                  {(bundles ?? []).map((b) => (
                    <option key={b.productCode} value={b.productCode}>
                      {b.name} — {b.currency ?? ''} {b.value}
                    </option>
                  ))}
                </select>
              </Field>
            )}

            <Field label={targetFieldFor(selected).label}>
              <input value={target} onChange={(e) => setTarget(e.target.value)} placeholder={targetFieldFor(selected).placeholder} style={inputStyle} />
            </Field>

            <Field label={`Amount${selected.currency ? ` (${selected.currency})` : ''}`}>
              <input
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputMode="decimal"
                placeholder="5.00"
                readOnly={!!selected.requiresProductCode}
                style={{ ...inputStyle, ...(selected.requiresProductCode ? { background: '#f3f4f6' } : {}) }}
              />
              {!selected.requiresProductCode && (
                <UsdCalculator rate={fxRate} currency={selected.currency}
                               onApply={(zwg) => setAmount(zwg)} />
              )}
            </Field>

            <Field label="Your EcoCash number">
              <input value={customerMsisdn} onChange={(e) => setCustomerMsisdn(e.target.value)} placeholder="0772929223" style={inputStyle} />
            </Field>

            {error && <p style={{ color: '#b91c1c', fontSize: 13, marginTop: 8 }}>{error}</p>}

            <button
              onClick={submit}
              disabled={!canSubmit || submitting}
              style={{
                width: '100%', marginTop: 16, padding: '12px 0', borderRadius: 8, border: 'none',
                background: canSubmit ? '#16a34a' : '#d1d5db', color: 'white', fontWeight: 600,
                fontSize: 15, cursor: canSubmit ? 'pointer' : 'not-allowed',
              }}
            >
              {submitting ? 'Sending payment request…' : 'Pay with EcoCash'}
            </button>
          </div>
        )}

        {session?.status === 'PENDING' && (
          <StatusCard tone="pending" title="Check your phone">
            An EcoCash payment prompt has been sent to {session.target}. Approve it to complete
            your recharge. This page updates automatically.
            <ReferenceNote id={session.id} />
          </StatusCard>
        )}
        {session?.status === 'PAID' && (
          <StatusCard tone="success" title="Recharge sent">
            Payment received — your recharge has been placed.
            <ReferenceNote id={session.id} />
          </StatusCard>
        )}
        {(session?.status === 'FAILED' || session?.status === 'EXPIRED') && (
          <StatusCard tone="failed" title="Payment not completed">
            {session.failureReason ?? 'The payment was not received in time.'}{' '}
            <button onClick={startOver} style={{ color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              Try again
            </button>
          </StatusCard>
        )}
      </div>

      <SidePanel />
      </div>

      <Footer />
    </div>
  );
}

function ReferenceNote({ id }: { id: string }) {
  return (
    <p style={{ marginTop: 10, fontSize: 12, opacity: 0.85 }}>
      Reference: <span style={{ fontFamily: 'monospace' }}>{id}</span> — save this to check back
      later at <Link to="/pay/status" style={{ color: 'inherit', textDecoration: 'underline' }}>
        /pay/status
      </Link>.
    </p>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block', marginBottom: 12 }}>
      <span style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>{label}</span>
      {children}
    </label>
  );
}

export function StatusCard({ tone, title, children }: { tone: 'pending' | 'success' | 'failed'; title: string; children: React.ReactNode }) {
  const colors = {
    pending: { bg: '#fffbeb', border: '#fde68a', text: '#92400e' },
    success: { bg: '#f0fdf4', border: '#bbf7d0', text: '#166534' },
    failed: { bg: '#fef2f2', border: '#fecaca', text: '#991b1b' },
  }[tone];
  return (
    <div style={{ background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 20 }}>
      <p style={{ fontWeight: 600, color: colors.text, marginBottom: 6 }}>{title}</p>
      <p style={{ fontSize: 13, color: colors.text }}>{children}</p>
    </div>
  );
}

export const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14,
};
